import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../../ui/dialog";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Textarea } from "../../../ui/textarea";
import { CateringCategoryGroup } from "../../../../hooks/useCateringMenus";
import { toast } from "sonner";

interface EditCategoryGroupDialogProps {
  group: CateringCategoryGroup | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (group:any, type:string, groupID?:string) => void;
}

export function EditCategoryGroupDialog({ group, open, onOpenChange, onSave }: EditCategoryGroupDialogProps) {
  const isCreate =  !group?.id;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [minSelection, setMinSelection] = useState<string>("");
  const [maxSelection, setMaxSelection] = useState<string>("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (group) {
      setName(group.name);
      setDescription(group.description || "");
      setMinSelection(group.min_selection != null ? String(group.min_selection) : "");
      setMaxSelection(group.max_selection != null ? String(group.max_selection) : "");
    } else {
      setName("");
      setDescription("");
      setMinSelection("");
      setMaxSelection("");
    }
  }, [group, open]);

  const handleSave = async () => {
    const payload = {
      name,
      description: description || null,
      min_selection: minSelection ? parseInt(minSelection) : null,
      max_selection: maxSelection ? parseInt(maxSelection) : null,
    };
    if (isCreate) {
      onSave(payload, 'add')      
    }else {
      onSave(payload, 'update', group.id)
    }   
  };

  const handleDelete = async () => {
    if (!group) return;
    setSaving(true);
   /*  const { error } = await supabase.from("catering_category_groups").delete().eq("id", group.id);
    setSaving(false);

    if (error) {
      toast.error("Failed to delete group");
      console.error(error);
    } else {
      toast.success("Category group deleted");
      onSave();
      onOpenChange(false);
    } */
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isCreate ? "Add Category Group" : "Edit Category Group"}</DialogTitle>
          <DialogDescription>
            {isCreate 
              ? "Create a parent group to organize subcategories (e.g. Starters, Mains)." 
              : "Update group name and description."
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="groupName">Group Name</Label>
            <Input
              id="groupName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Starters"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="groupDescription">Description (optional)</Label>
            <Textarea
              id="groupDescription"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this group"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="groupMinSelection">Min Selection</Label>
              <Input
                id="groupMinSelection"
                type="number"
                min="0"
                value={minSelection}
                onChange={(e) => setMinSelection(e.target.value)}
                placeholder="e.g., 1"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="groupMaxSelection">Max Selection</Label>
              <Input
                id="groupMaxSelection"
                type="number"
                min="0"
                value={maxSelection}
                onChange={(e) => setMaxSelection(e.target.value)}
                placeholder="e.g., 3"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="flex justify-between">
          {!isCreate && (
            <Button variant="destructive" size="sm" onClick={handleDelete} disabled={saving}>
              Delete Group
            </Button>
          )}
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !name.trim()}>
              {saving ? "Saving..." : isCreate ? "Create Group" : "Save Changes"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
